/**
 * useAct3Loop — 第三幕游戏循环 Hook
 *
 * v2: 支持 SSE 流式双视角内心独白，降级回退到 JSON 模式。
 */
import { useCallback, useRef } from 'react';
import { useAct3Store } from '../stores/act3Store';
import { useRootStore } from '../stores/rootStore';
import { act3Week, act3Evaluate } from '../services/api/v2GameApi';
import { connectSSE } from '../services/api/sseClient';
import { FlowController } from '../utils/flowController';
import type { StrategyId } from '@shared/types/cards';
import type { Act3WeekResponse } from '@shared/types/api';
import {
  ACT3_MAX_WEEKS,
  ACT3_COMMITMENT_FAIL,
  ACT3_ENERGY_FAIL,
  ACT3_STABILITY_FAIL,
  ACT3_COMMITMENT_WIN,
} from '@shared/constants/gameBalance';
import { SPECIAL_STRATEGIES } from '@shared/constants/act3Strategies';

export function useAct3Loop() {
  const act3 = useAct3Store();
  const root = useRootStore();
  const flowControllerRef = useRef(new FlowController());
  const abortRef = useRef<AbortController | null>(null);

  /** 检查是否提前结束 */
  const checkEnd = useCallback((): 'win' | 'fail' | null => {
    const s = useAct3Store.getState();
    const avgCommit = (s.commitmentA + s.commitmentB) / 2;
    const avgEnergy = (s.energyA + s.energyB) / 2;

    if (avgCommit >= ACT3_COMMITMENT_WIN) return 'win';
    if (avgCommit < ACT3_COMMITMENT_FAIL) return 'fail';
    if (avgEnergy < ACT3_ENERGY_FAIL) return 'fail';
    if (s.stability < ACT3_STABILITY_FAIL) return 'fail';
    return null;
  }, []);

  /** 初始化第三幕 */
  const initAct3 = useCallback(() => {
    const rootState = useRootStore.getState();
    const store = useAct3Store.getState();
    store.initialize(rootState.act2Result ?? undefined);
    store.setPhase('TRIAL_REVEAL');
  }, []);

  /** 揭示考验后进入策略选择 */
  const confirmTrial = useCallback(() => {
    useAct3Store.getState().setPhase('STRATEGY_SELECT');
  }, []);

  /** 应用周结果到 store */
  const applyWeekResult = useCallback((response: Act3WeekResponse) => {
    const store = useAct3Store.getState();
    store.adjustCommitment('A', response.commitmentChangeA);
    store.adjustCommitment('B', response.commitmentChangeB);
    store.adjustEnergy('A', response.energyChangeA);
    store.adjustEnergy('B', response.energyChangeB);
    store.adjustStability(response.stabilityChange);
    store.applyWeeklyRecovery();
    store.setWeekNarrative(response.narrative);
    store.addNarrative(response.narrative);
    if (response.eventDescription) {
      store.addNarrative(`[事件] ${response.eventDescription}`);
    }
  }, []);

  /** 降级到 JSON 模式 */
  const fallbackToJsonMode = useCallback(async (strategy: StrategyId) => {
    const rootState = useRootStore.getState();
    const store = useAct3Store.getState();
    if (!rootState.protagonistA || !rootState.protagonistB) return;

    const activeTrial = store.trials[store.activeTrialIndex];
    if (!activeTrial) return;

    try {
      const response = await act3Week({
        protagonistA: rootState.protagonistA,
        protagonistB: rootState.protagonistB,
        week: store.week,
        commitmentA: store.commitmentA,
        commitmentB: store.commitmentB,
        energyA: store.energyA,
        energyB: store.energyB,
        stability: store.stability,
        activeTrialId: activeTrial.id,
        chosenStrategy: strategy,
        narrativeLog: store.narrativeLog,
        act2Result: rootState.act2Result ?? undefined,
      });

      applyWeekResult(response);
      store.setIsProcessing(false);
      store.setPhase('WEEK_RESULT');
    } catch (err) {
      console.error('Act3 fallback also failed:', err);
      store.setWeekNarrative('这一周在沉默中度过...');
      store.addNarrative('这一周在沉默中度过...');
      store.setIsProcessing(false);
      store.setPhase('WEEK_RESULT');
    }
  }, [applyWeekResult]);

  /** 选择策略并执行一周（SSE 流式模式） */
  const executeWeek = useCallback(async (strategy: StrategyId) => {
    const rootState = useRootStore.getState();
    const store = useAct3Store.getState();
    if (!rootState.protagonistA || !rootState.protagonistB) return;

    const activeTrial = store.trials[store.activeTrialIndex];
    if (!activeTrial) return;

    // Mark special strategy as used
    const isSpecial = SPECIAL_STRATEGIES.some(s => s.id === strategy);
    if (isSpecial) {
      store.markSpecialUsed(strategy);
    }

    store.chooseStrategy(strategy);
    store.setPhase('PROCESSING');
    store.setIsProcessing(true);

    // Apply auto damage from trial
    store.adjustCommitment('A', activeTrial.autoDamage.commitment);
    store.adjustCommitment('B', activeTrial.autoDamage.commitment);
    store.adjustEnergy('A', activeTrial.autoDamage.energy);
    store.adjustEnergy('B', activeTrial.autoDamage.energy);
    store.adjustStability(activeTrial.autoDamage.stability);

    // 中断之前的流
    flowControllerRef.current.interrupt();
    if (abortRef.current) abortRef.current.abort();

    const flowToken = flowControllerRef.current.getToken();

    const requestBody = {
      protagonistA: rootState.protagonistA,
      protagonistB: rootState.protagonistB,
      week: store.week,
      commitmentA: store.commitmentA,
      commitmentB: store.commitmentB,
      energyA: store.energyA,
      energyB: store.energyB,
      stability: store.stability,
      activeTrialId: activeTrial.id,
      chosenStrategy: strategy,
      narrativeLog: store.narrativeLog,
      act2Result: rootState.act2Result ?? undefined,
    };

    const abortCtrl = connectSSE('/api/act3/week-enhanced', requestBody, {
      onWeekNarrative: (data) => {
        if (!flowToken.isValid()) return;
        const s = useAct3Store.getState();
        applyWeekResult(data);
      },

      onMonologueStart: (side, name) => {
        if (!flowToken.isValid()) return;
        useAct3Store.getState().setStreamingMonologueSide(side);
      },

      onMonologueChunk: (side, text) => {
        if (!flowToken.isValid()) return;
        const s = useAct3Store.getState();
        if (s.streamingMonologueSide === side) {
          s.appendStreamingMonologue(text);
        }
      },

      onMonologueEnd: (side, fullText) => {
        if (!flowToken.isValid()) return;
        const s = useAct3Store.getState();
        s.setInnerMonologue(side, fullText);
        s.clearStreamingMonologue();
      },

      onWeekComplete: () => {
        if (!flowToken.isValid()) return;
        const s = useAct3Store.getState();
        s.setIsProcessing(false);
        s.setPhase('WEEK_RESULT');
      },

      onError: (err) => {
        console.error('Act3 SSE error, falling back to JSON:', err);
        fallbackToJsonMode(strategy);
      },
    });

    abortRef.current = abortCtrl;
  }, [fallbackToJsonMode]);

  /** 确认周结果，进入下一周或结束 */
  const confirmWeekResult = useCallback(async () => {
    const store = useAct3Store.getState();
    const endResult = checkEnd();

    if (endResult || store.week >= ACT3_MAX_WEEKS) {
      await finishAct3();
      return;
    }

    store.nextWeek();

    // Check if current trial duration is exhausted, advance to next
    const activeTrial = store.trials[store.activeTrialIndex];
    const weeksOnTrial = store.week - 1;
    if (activeTrial && weeksOnTrial >= activeTrial.duration && store.activeTrialIndex < store.trials.length - 1) {
      store.advanceTrial();
    }

    // Clear monologues for next week
    store.setInnerMonologue('A', '');
    store.setInnerMonologue('B', '');

    store.setPhase('TRIAL_REVEAL');
  }, [checkEnd]);

  /** 结束第三幕 */
  const finishAct3 = useCallback(async () => {
    const rootState = useRootStore.getState();
    const store = useAct3Store.getState();
    if (!rootState.protagonistA || !rootState.protagonistB) return;

    store.setPhase('FINISHED');
    store.setIsProcessing(true);

    try {
      const response = await act3Evaluate({
        protagonistA: rootState.protagonistA,
        protagonistB: rootState.protagonistB,
        commitmentA: store.commitmentA,
        commitmentB: store.commitmentB,
        energyA: store.energyA,
        energyB: store.energyB,
        stability: store.stability,
        narrativeLog: store.narrativeLog,
        act2Result: rootState.act2Result ?? undefined,
      });

      store.setIsProcessing(false);
      rootState.finishAct3(response.result);
    } catch (err) {
      console.error('Act3 evaluate failed:', err);
      store.setIsProcessing(false);
      rootState.finishAct3({
        outcome: '理性止损',
        finalCommitmentA: store.commitmentA,
        finalCommitmentB: store.commitmentB,
        finalStability: store.stability,
        highlights: ['关系在考验中走到了终点'],
        title: '缘分天注定',
        narrative: '这段关系最终走向了它注定的结局。',
        score: 'C',
      });
    }
  }, []);

  return {
    initAct3,
    confirmTrial,
    executeWeek,
    confirmWeekResult,
    phase: act3.phase,
    week: act3.week,
  };
}
